/**
 * Microsoft Clarity wrapper with environment guard and double-init protection.
 * All calls are no-ops if VITE_CLARITY_PROJECT_ID is not set or Clarity is blocked.
 */

import Clarity from '@microsoft/clarity';

const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;

let initialized = false;

/**
 * Initialize Clarity session recording.
 * Guards against missing project ID and double initialization.
 * Warns in development if the project ID is missing.
 */
export function initClarity(): void {
  if (initialized) return;

  if (!CLARITY_PROJECT_ID) {
    if (import.meta.env.DEV) {
      console.warn('[clarity] VITE_CLARITY_PROJECT_ID is not set — Clarity will not record.');
    }
    return;
  }

  Clarity.init(CLARITY_PROJECT_ID);
  initialized = true;
}

/**
 * Identify the current user for Clarity session tagging.
 * Pass userId at minimum; optional sessionId, pageId, and friendlyName
 * are forwarded to Clarity.identify().
 */
export function identifyClarityUser(
  userId: string,
  sessionId?: string,
  pageId?: string,
  friendlyName?: string,
): void {
  if (!CLARITY_PROJECT_ID) return;
  Clarity.identify(userId, sessionId, pageId, friendlyName);
}

/**
 * Set a custom tag on the current Clarity session.
 * Useful for segmenting recordings by plan, feature, or experiment.
 */
export function setClarityTag(key: string, value: string): void {
  if (!CLARITY_PROJECT_ID) return;
  Clarity.setTag(key, value);
}
